import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./document.module.css";
import actionsIcon from "../../images/actions_icon.svg";
import personIcon from "../../images/person_icon.svg";
import sendIcon from "../../images/chat_send_icon.svg";
import groupUsersIcon from "../../images/group_users_icon_black.svg";
import useAutosizeTextarea from "../../hooks/useAutosizeTextarea";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import { TAppointmentsProgramsData } from "../../services/types/utils.types";
import { noDataLabel } from "../../utils/constants";
import { getPrintDate } from "../../utils/utils";
import { wrapUserForReport, wrapUserWithDualCase } from "../../utils/user";
import Modal from "../modals/modal/Modal";
import PrivacyProtectionPopup from "../modals/privacyProtectionPopup/privacyProtectionPopup";
import AddTreatmentPlan from "../addTreatmentPlan/addTreatmentPlan";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import {
  usePostCommentsToAppointmentsProgramsMutation,
  useGetAppointmentsProgramsCommentsQuery,
  useGetAllMetaTablesQuery,
} from "../../services/api/utils.api";
import { IUser } from "../../types/types";
import i18n from "../../i18n";

interface IDocumentProps {
  openDocument: boolean;
  treatmentPlan: TAppointmentsProgramsData;
  onClose: () => void;
  readOnly?: boolean;
}

type StatusType = "pending" | "waiting" | "approved" | "unknown";

function TreatmentDocument({ treatmentPlan, openDocument, onClose, readOnly = false }: IDocumentProps) {
  const ap = treatmentPlan?.appointmentsProgram;
  const user = treatmentPlan?.user;
  const admin = useAppSelector(adminSelector);
  const navigate = useNavigate();
  const isGroupTreatment = !!ap?.treatmentGroupId;
  const [openPrivacyPopup, setOpenPrivacyPopup] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<IUser | null>(null);
  const [errorModal, setErrorModal] = useState<{ message: string } | null>(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openEditTreatmentPlan, setOpenEditTreatmentPlan] = useState(false);

  const [commentText, setCommentText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextarea(textareaRef.current, commentText);

  const [postComment, { isLoading: isPostingComment }] = usePostCommentsToAppointmentsProgramsMutation();
  const { data: serverComments, refetch: refetchComments, isLoading: isLoadingComments } =
    useGetAppointmentsProgramsCommentsQuery(ap?.id, { skip: !ap?.id });

  useGetAllMetaTablesQuery(admin.companyId, { skip: !admin.companyId });

  // Computed Values - Status
  const statusType: StatusType = !ap
    ? "unknown"
    : !ap.isApproved
      ? "pending"
      : ap.appointmentsDurationsId && !ap.therapistsId
        ? "waiting"
        : ap.therapistsId
          ? "approved"
          : "unknown";

  const status = statusType === "pending" ? t('PENDING')
    : statusType === "waiting" ? t('WAITING_FOR_THERAPIST_1')
      : statusType === "approved" ? t('APPROVED')
        : t(noDataLabel);

  const programCreateDate = ap?.createDate ? getPrintDate(new Date(ap.createDate)) : t(noDataLabel);
  const patientName = isGroupTreatment
    ? treatmentPlan.treatmentGroup?.groupName || t(noDataLabel)
    : user?.fullName || ap?.name || t(noDataLabel);
  const patientId = isGroupTreatment
    ? treatmentPlan.treatmentGroup?.id || t(noDataLabel)
    : user?.id || ap?.questionnaireCompaniesScoreUsersId || t(noDataLabel);
  const treatmentMethod = treatmentPlan.mediaName || treatmentPlan?.media?.name || t(noDataLabel);
  const firstParticipantWithGuid = treatmentPlan.additionalUsers?.find((p) => p?.Guid || p?.guid);
  const firstParticipantGuid = firstParticipantWithGuid?.Guid || firstParticipantWithGuid?.guid;
  const userGuid = user?.Guid || user?.guid || firstParticipantGuid || "0";
  const participantsCount = treatmentPlan.additionalUsers?.length || 0;

  // Event Handlers
  const handleAddComment = async () => {
    const missingCommentContext =
      !ap ||
      !admin ||
      userGuid === "0" ||
      (!user && !(isGroupTreatment && treatmentPlan.additionalUsers?.length));

    if (!commentText.trim() || missingCommentContext) {
      if (!commentText.trim()) return;
      setErrorModal({ message: t('MISSING_DATA_FOR_COMMENT') });
      return;
    }

    try {
      const response = await postComment({
        AppointmentsProgramsId: ap.id,
        UsersGuid: userGuid,
        TherapistsGuid: admin.guid,
        Comment: commentText,
      });

      if (response && !('error' in response)) {
        setCommentText("");
        refetchComments();
        setOpenSuccessModal(true);
      } else {
        setErrorModal({ message: t('ERROR_ADDING_COMMENT') });
      }
    } catch {
      setErrorModal({ message: t('ERROR_ADDING_COMMENT') });
    }
  };

  const handlePrivacyPopupClose = () => {
    const targetUser = selectedParticipant || user;
    const guid = targetUser?.Guid || targetUser?.guid || "0";

    if (guid === "0") {
      setErrorModal({ message: t('USER_ID_NOT_FOUND') });
      return;
    }

    const wrappedUser = wrapUserForReport({ ...targetUser, treatmentGroupId: ap?.treatmentGroupId }, guid);

    // Navigate to EditUserDataPage with user data
    navigate("/users/edit", { state: { user: wrappedUser } });
    setOpenPrivacyPopup(false);
    setSelectedParticipant(null);
  };

  const handleParticipantClick = (participant: IUser) => {
    setSelectedParticipant({ ...participant, treatmentGroupId: treatmentPlan.treatmentGroupId });
    setOpenPrivacyPopup(true);
  };

  const normalizedComments = serverComments?.map((c) => ({
    comment: c.comment,
    therapistName: c.therapistName,
    createOn: c.createOn,
  })) ?? [];

  return (
    <article className={`${styles.document} ${openDocument ? styles.document_visible : ""}`}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderTop}>
          <h2 className={styles.cardTitle}>
            {t('PROGRAM_NUMBER_1')} {ap?.id ?? t(noDataLabel)}
          </h2>
          <button className={styles.documentCloseBtn} onClick={onClose} title={t('CLOSE_TICKET')}>
            ✖
          </button>
        </div>
        <div className={styles.statusSection}>
          <div className={`${styles.statusBadge} ${statusType === "approved" ? styles.statusApproved : styles.statusPending
            }`}>
            {status}
          </div>
        </div>
      </div>

      <div dir={i18n.dir()} className={styles.cardContent}>
        {/* Patient Details */}
        <InfoSection title={t('PATIENT_DETAILS')} iconClass={styles.iconPersonColor}>
          <InfoRow label={isGroupTreatment ? t('GROUP_NAME') : t('PATIENT_NAME_1')} value={patientName} />
          <InfoRow label={isGroupTreatment ? t('GROUP_ID') : t('PATIENT_IDENTIFIER')} value={patientId} />
          {isGroupTreatment && (
            <InfoRow label={t('NUMBER_OF_PARTICIPANTS')} value={participantsCount} />
          )}
          <InfoRow label={t('OPENING_DATE')} value={programCreateDate} />
        </InfoSection>

        {/* Treatment Details - Conditional based on status */}
        {statusType === "pending" && (
          <InfoSection title={t('TREATMENT_DETAILS')} iconClass={styles.iconActions}>
            <InfoRow label={t('THERAPIST_REPORT')} value={treatmentPlan?.createUserName ?? t(noDataLabel)} />
          </InfoSection>
        )}

        {(statusType === "waiting" || statusType === "approved") && (
          <InfoSection title={t('TREATMENT_DETAILS')} iconClass={styles.iconActions}>
            <InfoRow label={t('TREATMENT_NAME')} value={ap?.name ?? t(noDataLabel)} />
            <InfoRow label={t('TREATMENT_METHOD:')} value={treatmentMethod} />
            <InfoRow label={t('THERAPIST_REPORT')} value={treatmentPlan?.createUserName ?? t(noDataLabel)} />
          </InfoSection>
        )}

        {/* Recommendations */}

        <InfoSection title={t('RECOMMENDATIONS')} iconClass={styles.iconRecommend}>
          <span className={styles.infoLabel}>{t('RECOMMENDATIONS_1')}</span>
          <span className={styles.infoValue}>{treatmentPlan?.recommendations || t(noDataLabel)}</span>
          {/* <InfoRow 
              label={t('RECOMMENDATIONS_1')} 
              value={treatmentPlan?.recommendations || t(noDataLabel)} 
            /> */}
        </InfoSection>


        {/* Notes/Comments */}
        <InfoSection title={t('NOTES')} iconClass={styles.iconNotes}>
          <CommentsList
            comments={normalizedComments}
            isLoading={isLoadingComments}
          />

          <CommentForm
            textareaRef={textareaRef}
            commentText={commentText}
            setCommentText={setCommentText}
            onSubmit={handleAddComment}
            isSubmitting={isPostingComment}
            disabled={readOnly}
          />
        </InfoSection>

        {/* Participants Section - Only for Group Treatment */}
        {isGroupTreatment && treatmentPlan.additionalUsers && treatmentPlan.additionalUsers.length > 0 && (
          <InfoSection title={t('PARTICIPANTS')} iconClass={styles.iconPersonColor}>
            <div className={styles.participantsList}>
              {treatmentPlan.additionalUsers.map((participant, idx) => (
                <button
                  key={participant.id || idx}
                  className={styles.participantBtn}
                  type="button"
                  onClick={() => handleParticipantClick(participant)}
                >
                  <img src={groupUsersIcon} alt="" className={styles.participant_icon} />
                  {participant.fullName || `${participant.firstName || ''} ${participant.lastName || ''}`.trim()}
                </button>
              ))}
            </div>
          </InfoSection>
        )}

        {/* Additional Actions */}
        <InfoSection title={t('ADDITIONAL_ACTIONS')} iconClass={styles.iconMoreActions}>
          <div className={styles.actionButtons}>
            {!isGroupTreatment && (
              <button
                className={`${styles.view_btn}`}
                type="button"
                onClick={() => setOpenPrivacyPopup(true)}
              >
                <img src={personIcon} alt={t('PTEEK_TIK')} className={styles.view_icon} />
                {t('PTEEK_TIK')}
              </button>
            )}

            {!readOnly && (
              <button
                className={`${styles.view_btn}`}
                type="button"
                onClick={() => setOpenEditTreatmentPlan(true)}
              >
                <img src={actionsIcon} alt={t('OPEN_EDIT')} className={styles.view_icon} />
                {t('OPEN_EDIT')}
              </button>
            )}

          </div>
        </InfoSection>
      </div>

      {/* Modals */}
      {openPrivacyPopup && (selectedParticipant || user) && (
        <Modal onClose={() => { setOpenPrivacyPopup(false); setSelectedParticipant(null); }}>
          <PrivacyProtectionPopup
            onClose={handlePrivacyPopupClose}
            clientUsersGuid={selectedParticipant?.guid || selectedParticipant?.Guid || userGuid}
          />
        </Modal>
      )}

      {openSuccessModal && (
        <Modal onClose={() => setOpenSuccessModal(false)}>
          <GeneralSuccessPopup onClose={() => { setOpenSuccessModal(false); onClose(); }} />
        </Modal>
      )}

      {errorModal && (
        <Modal onClose={() => setErrorModal(null)}>
          <ErrorModal message={errorModal.message} onClose={() => setErrorModal(null)} />
        </Modal>
      )}

      {openEditTreatmentPlan && (
        <Modal onClose={() => setOpenEditTreatmentPlan(false)} wide>
          <AddTreatmentPlan
            onClose={() => setOpenEditTreatmentPlan(false)}
            user={wrapUserWithDualCase(user || {}, userGuid, user?.companyId || admin.companyId) as any}
            program={treatmentPlan}
            submitButtonText="APPROVE"
          />
        </Modal>
      )}
    </article>
  );
}

// Sub-components for better organization
function InfoSection({ title, iconClass, children }: { title: string; iconClass: string; children: React.ReactNode }) {
  return (
    <div className={styles.infoSection}>
      <h3 className={`${styles.sectionTitle} ${styles.infoTitle}`}>
        <span className={`${styles.sectionIcon} ${iconClass}`} />
        {title}
      </h3>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value}</span>
    </div>
  );
}

function CommentsList({ comments, isLoading }: { comments: any[]; isLoading: boolean }) {
  if (isLoading) return <span className={styles.commentContent}>{t('LOADING_COMMENTS')}</span>;
  if (!comments.length) return (
    <span className={styles.commentContent}>{t("NO_NOTES_YET")}</span>
  );

  return (
    <div className={styles.commentsList}>
      {comments.map((c, idx) => (
        <div key={idx} className={styles.commentItem}>
          <div className={styles.commentText}>
            <span className={styles.commentLabel}>{t('NOTE')}</span>
            <span className={styles.commentContent}>
              {c.comment?.trim() || t('NO_CONTENT_FOR_COMMENT')}
            </span>
          </div>
          <div className={styles.commentMeta}>
            <span>{c.therapistName?.trim() || t('UNKNOWN')}</span>
            <span>{c.createOn ? getPrintDate(new Date(c.createOn)) : t('WITHOUT_DATE')}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CommentForm({ textareaRef, commentText, setCommentText, onSubmit, isSubmitting, disabled }: any) {
  return (
    <form className={styles.commentsForm} onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      <div className={styles.commentInputWrapper} >
        <textarea
          ref={textareaRef}
          className={styles.commentsTextarea}
          placeholder={t('ADD_NEW_COMMENT')}
          rows={1}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          disabled={disabled}
        />
        <button
          className={styles.sendCommentBtn}
          type="submit"
          disabled={disabled || isSubmitting || !commentText.trim()}
          aria-label={t('SEND_COMMENT')}
        >
          <img src={sendIcon} alt={t('SEND')} />
        </button>
      </div>
    </form>
  );
}

function ErrorModal({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className={styles.errorModalContainer}>
      <div className={styles.errorModalTitle}>{t('ERROR_MESSAGE')}</div>
      <div className={styles.errorModalMessage}>{message}</div>
      <button className={styles.errorModalCloseBtn} onClick={onClose}>{t('CLOSE')}</button>
    </div>
  );
}

export default TreatmentDocument;